import React, { useState, useEffect, useCallback } from 'react';
import { FaProcedures, FaPlus, FaTimes, FaExchangeAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import AddBedTypeModal from './AddBedTypeModal';

const BedTypesModal = ({ isOpen, onClose, rooms = [], onUpdateBedTypes }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [typesList, setTypesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBedTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(ENDPOINTS.GET_ALL_BED_TYPES);
      const data = Array.isArray(res.data) ? res.data : [];
      setTypesList(data);
      if (onUpdateBedTypes) onUpdateBedTypes(data);
    } catch (err) {
      console.error('Failed to fetch bed types', err);
      toast.error('Failed to load bed types from database');
    } finally {
      setLoading(false);
    }
  }, [onUpdateBedTypes]);

  useEffect(() => {
    if (isOpen) {
      fetchBedTypes();
    }
  }, [isOpen, fetchBedTypes]);

  if (!isOpen) return null;

  const typeStats = typesList.map((bt) => {
    const roomsForBedType = rooms.filter(
      (r) => (r.bedType || '').toLowerCase() === (bt.name || '').toLowerCase()
    );
    const total = roomsForBedType.reduce((acc, r) => acc + Number(r.totalBeds || 1), 0);
    const occupied = roomsForBedType
      .filter((r) => r.status === 'Occupied' || (r.occupiedBeds && r.occupiedBeds > 0))
      .reduce((acc, r) => acc + (r.status === 'Occupied' ? Number(r.totalBeds || 1) : Number(r.occupiedBeds || 0)), 0);

    const isTypeInactive = bt.status === 'Inactive';
    const inactive = isTypeInactive
      ? (total - occupied)
      : roomsForBedType
          .filter((r) => r.status === 'Under Maintenance' || r.status === 'Inactive' || r.active === false)
          .reduce((acc, r) => acc + Number(r.totalBeds || 1), 0);
    const vacant = isTypeInactive ? 0 : Math.max(0, total - occupied - inactive);

    return {
      ...bt,
      occupied,
      vacant,
      inactive,
      total
    };
  });

  const handleToggleStatus = async (typeId) => {
    const targetType = typeStats.find((t) => t.id === typeId);

    // Data Consistency Protection: Do not allow deactivating bed types with occupied beds
    if (targetType && targetType.status === 'Active' && targetType.occupied > 0) {
      toast.error(
        `Cannot deactivate "${targetType.name}" because ${targetType.occupied} bed(s) are currently occupied by admitted patients!`
      );
      return;
    }

    try {
      const res = await axios.patch(ENDPOINTS.TOGGLE_BED_TYPE_STATUS(typeId));
      const updatedItem = res.data;
      const updated = typesList.map((bt) => (bt.id === typeId ? updatedItem : bt));
      setTypesList(updated);
      if (onUpdateBedTypes) onUpdateBedTypes(updated);
      toast.info(`Bed Type "${updatedItem.name}" is now ${updatedItem.status}`);
    } catch (err) {
      console.error('Failed to toggle bed type status', err);
      toast.error('Failed to update bed type status');
    }
  };

  const handleAddBedType = async (newTypePayload) => {
    try {
      const res = await axios.post(ENDPOINTS.ADD_BED_TYPE, {
        name: newTypePayload.name,
        description: newTypePayload.description || `Hospital ${newTypePayload.name}`,
        status: newTypePayload.status || 'Active'
      });
      const created = res.data;
      const updated = [...typesList, created];
      setTypesList(updated);
      if (onUpdateBedTypes) onUpdateBedTypes(updated);
      toast.success(`Bed Type "${created.name}" created successfully in database!`);
    } catch (err) {
      console.error('Failed to add bed type', err);
      const msg = err.response?.data?.message || err.message || 'Failed to add bed type';
      toast.error(msg);
    }
  };

  return (
    <>
      <div className="manage-modal-backdrop" onClick={onClose}>
        <div className="floors-modal-master-card" style={{ maxWidth: '850px' }} onClick={(e) => e.stopPropagation()}>
          {/* Top Accent Gradient Bar */}
          <div className="floors-modal-accent-bar" />

          {/* Modal Header */}
          <div className="floors-modal-header">
            <h3>
              <FaProcedures color="#2563eb" size={18} /> Bed Types
            </h3>
            <button className="floors-modal-close-btn" onClick={onClose} title="Close">
              <FaTimes size={16} />
            </button>
          </div>

          {/* Modal Body */}
          <div className="floors-modal-body">
            {/* Control Bar: Add Bed Type Action */}
            <div className="floors-modal-controls" style={{ marginBottom: '16px' }}>
              <div className="floors-metrics-row">
                <span className="floors-metric-label">
                  Configured Bed Types: <span className="floors-metric-value">{typesList.length}</span>
                </span>
                {loading && <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '10px' }}>Loading...</span>}
              </div>
              <button
                type="button"
                className="btn-add-floor-primary"
                onClick={() => setShowAddModal(true)}
              >
                <FaPlus size={11} /> Add Bed Type
              </button>
            </div>

            {/* Clean Synced Table */}
            <div className="floors-table-wrapper">
              <table className="floors-synced-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', paddingLeft: '20px' }}>BED TYPE</th>
                    <th>OCCUPIED BEDS</th>
                    <th>VACANT BEDS</th>
                    <th>INACTIVE BEDS</th>
                    <th>TOTAL BEDS</th>
                    <th>STATUS</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {typeStats.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ padding: '30px', color: '#64748b', fontStyle: 'italic' }}>
                        {loading ? 'Loading bed types from database...' : 'No bed types configured.'}
                      </td>
                    </tr>
                  ) : (
                    typeStats.map((row) => (
                      <tr key={row.id}>
                        <td style={{ textAlign: 'left', paddingLeft: '20px', fontWeight: '700', color: '#0f172a', whiteSpace: 'nowrap' }}>
                          {row.name.toUpperCase()}
                        </td>
                        <td className="text-occupied">{row.occupied}</td>
                        <td className="text-vacant">{row.vacant}</td>
                        <td className="text-inactive">{row.inactive}</td>
                        <td className="text-total">{row.total}</td>
                        <td>
                          <span className={`status-chip ${row.status === 'Active' ? 'active' : 'inactive'}`}>
                            {row.status}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            style={{
                              background: row.status === 'Active' ? '#fff7ed' : '#f0fdf4',
                              color: row.status === 'Active' ? '#c2410c' : '#15803d',
                              border: `1px solid ${row.status === 'Active' ? '#fed7aa' : '#86efac'}`,
                              fontWeight: '600',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.18s ease'
                            }}
                            onClick={() => handleToggleStatus(row.id)}
                            title={
                              row.status === 'Active' && row.occupied > 0
                                ? 'Cannot deactivate while beds are occupied by admitted patients'
                                : `Click to set status to ${row.status === 'Active' ? 'Inactive' : 'Active'}`
                            }
                          >
                            <FaExchangeAlt size={11} /> {row.status === 'Active' ? 'Make Inactive' : 'Make Active'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="floors-modal-footer">
            <button type="button" className="btn-close-app" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Add Bed Type Sub-Modal */}
      <AddBedTypeModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSaveBedType={handleAddBedType}
      />
    </>
  );
};

export default BedTypesModal;
